import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Commission } from '../types/commission.types'
import { commissionsService } from '../../../shared/api/services/commissions.service'

export const useCommissions = () => {
    const [searchText, setSearchText] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(15)
    const [statusFilter, setStatusFilter] = useState<string>('all')

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['commissions', page, pageSize, statusFilter],
        queryFn: async () => {
            const filters = statusFilter !== 'all' ? { status: statusFilter } : {}
            return await commissionsService.getCommissions(page, pageSize, filters)
        },
    })

    const commissions: Commission[] = data?.data.map(rule => ({
        ...rule, // Spread is risky if backend has extra fields, better explicit
        id: rule.id.toString(),
        scope: rule.scope || 'product',
        productId: undefined, // Legacy support removed
        productName: '', // Legacy
        products: rule.products || [],
        category: rule.category?.name || (rule.category_id ? `Category #${rule.category_id}` : ''),
        sellerId: rule.seller_id,
        subscriptionPlanId: rule.subscription_plan_id,
        campaignId: rule.campaign_id,
        commissionType: rule.commission_type,
        commissionValue: rule.commission_value,
        minOrderValue: rule.min_order_value,
        maxOrderValue: rule.max_order_value,
        minQuantity: rule.min_quantity,
        maxQuantity: rule.max_quantity,
        priority: rule.priority,
        effectiveFrom: rule.effective_from,
        effectiveTo: rule.effective_to,
        status: rule.status,
        createdAt: new Date(rule.created_at).toLocaleDateString(),
        updatedAt: new Date(rule.updated_at).toLocaleDateString(),
    })) || []

    const filteredCommissions = useMemo(() => {
        if (!searchText) return commissions
        const lowerSearch = searchText.toLowerCase()
        return commissions.filter(c => 
            c.productName.toLowerCase().includes(lowerSearch) ||
            c.category.toLowerCase().includes(lowerSearch) ||
            c.id.toLowerCase().includes(lowerSearch)
        )
    }, [commissions, searchText])

    return {
        commissions: filteredCommissions,
        searchText,
        setSearchText,
        isLoading,
        refetch,
        page,
        setPage,
        pageSize,
        setPageSize,
        statusFilter,
        setStatusFilter,
        totalCommissions: data?.meta.total || 0,
    }
}
